// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { classList } from '@Helpers/className';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { ICONS } from '@Constants/iconsApp';
import { Space } from 'antd';

import './FormSubmitButton.scss';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';

export interface FormSubmitOptions {
    another?: boolean;
}

interface FormSubmitButtonProps {
    footerText?: string;
    disabled: boolean;
    footer?: boolean;
    hidden?: boolean;
    formName?: string;
    onSubmit?: () => void;
    onCancel?: () => void;
    cancelText?: string;
    onAnother?: (() => void) | boolean;
    anotherText?: string;
    anotherConfirm?: string;
    anotherDisabled?: boolean;
    anotherLoading?: boolean;
    warning?: boolean;
    loading?: boolean;
}

export const FormSubmitButton = ({
    footerText,
    disabled,
    footer = true,
    hidden = false,
    formName,
    onSubmit,
    onCancel,
    cancelText,
    onAnother,
    anotherText,
    anotherConfirm,
    anotherDisabled = disabled,
    warning = false,
    loading = false,
    anotherLoading = loading,
}: FormSubmitButtonProps): ReactElement => {
    const i = useLocalMessage();

    const another = (
        <THButton
            type="default"
            onClick={typeof onAnother !== 'boolean' ? (!anotherConfirm ? onAnother : undefined) : undefined}
            disabled={anotherDisabled}
            form={formName}
            loading={anotherLoading}
        >
            {anotherText || i('general.another')}
        </THButton>
    );

    const Content = (
        <THForm.Item className="form-submit-button__form-item" noStyle shouldUpdate>
            {onCancel && (
                <THButton type="link" color="red" onClick={onCancel} disabled={loading}>
                    {cancelText || i('general.cancel')}
                </THButton>
            )}

            {onAnother && (
                <>
                    {anotherConfirm ? (
                        <THPopConfirm
                            title={anotherConfirm}
                            onConfirm={typeof onAnother !== 'boolean' ? onAnother : undefined}
                        >
                            {another}
                        </THPopConfirm>
                    ) : (
                        another
                    )}
                    <THForm.Item name="another" noStyle>
                        <THInput hidden />
                    </THForm.Item>
                </>
            )}

            <THButton
                className="form-submit-button__button"
                type="primary"
                disabled={disabled}
                htmlType="submit"
                form={formName}
                onClick={onSubmit}
                loading={loading}
                {...{ 'data-testid': 'form-submit' }}
            >
                {footerText || i('general.confirm')}
            </THButton>
        </THForm.Item>
    );

    return (
        <footer
            className={classList({
                'form-submit-button': footer,
                'form-submit-button__hidden': hidden,
                'form-submit-button__warning': warning,
            })}
        >
            <div className="form-submit-button__content">
                {warning ? (
                    <>
                        <div className="form-submit-button__warning-message">
                            <Space>
                                <ICONS.WarningOutlined className="form-submit-button__warning-icon" />
                                {i('notification.modification')}
                            </Space>
                        </div>
                        <div>{Content}</div>
                    </>
                ) : (
                    Content
                )}
            </div>
        </footer>
    );
};
